/** @jsxImportSource @emotion/react */
import * as d3 from 'd3'
import { useCallback, useEffect, useRef, useState } from 'react'
import { ChartTooltip } from './ChartTooltip'
import { round } from './utils/utils'
import { AxisCalculator } from './utils/AxisCalculator'
import { Box } from '@mui/material'
import { Circle, ErrorOutline } from '@mui/icons-material'
import ReactDOMServer from 'react-dom/server'

export interface ChartDataItem {
  label: string
  min?: number
  value: number
  max?: number
  color: string
  uncertainty?: {
    is_uncertain: boolean
    tooltip: string
  }
}

export const MakeBarChartSvg = (
  element: HTMLElement,
  data: ChartDataItem[],
  xTitle = '',
  yTitle = '',
  fullWidth = 300,
  updateTooltip?: (args: { show: boolean; data?: ChartDataItem; uncertaintyText?: string }) => void,
): { yAxisScale: d3.ScaleBand<string>; xAxisScale: d3.ScaleLinear<number, number> } => {
  // set the dimensions and margins of the graph
  const xCalculator = new AxisCalculator(
    data.flatMap((item) => [item.max ?? 0, item.min ?? 0, item.value]),
  )
  const fullHeight = Math.max((data.length + 1) * 40, 96)
  const rightMargin =
    xCalculator.roundValueForDisplay(Math.max(...data.map((d) => d.value))).length * 8 + 30
  const margin = { top: 0, right: rightMargin, bottom: 35, left: 60 },
    width = fullWidth - margin.left - margin.right,
    height = fullHeight - margin.top - margin.bottom

  const iconHTML = ReactDOMServer.renderToString(
    <>
      <Circle
        style={{
          color: 'white',
        }}
        height={24}
        width={24}
      />
      <ErrorOutline
        style={{
          color: '#0B2948',
        }}
        height={24}
        width={24}
      />
    </>,
  )

  d3.select(element).selectAll('svg').remove()
  const svg = d3
    .select(element)
    .append('svg')
    .attr('version', '1.1')
    .attr('xmlns', 'http://www.w3.org/2000/svg')
    .attr('width', fullWidth)
    .attr('height', fullHeight)
    .attr('font-family', 'Inter')
    .attr('font-size', '8pt')
    .attr('viewBox', `0 0 ${fullWidth} ${fullHeight}`)
    .attr('color', '#8392a1')
    .append('g')
    .attr('transform', `translate(${margin.left},${margin.top})`)

  const yAxisScale = d3
    .scaleBand()
    .range([height, 0])
    .domain(data.map((d) => d.label).reverse())
    .padding(0.2)

  const xAxisScale = d3.scaleLinear().domain(xCalculator.domain).range([0, width])

  // Axises
  svg.append('g').call(d3.axisLeft(yAxisScale).tickSize(0))
  svg
    .append('g')
    .attr('transform', `translate(0, ${height})`)
    .call(
      d3
        .axisBottom(xAxisScale)
        .ticks(xCalculator.tickCount)
        .tickSize(2)
        .tickSizeOuter(0)
        .tickFormat((t) => xCalculator.roundValue(t.valueOf())),
    )
    .selectAll('.tick line')
    .attr('y2', -height)
    .attr('y1', -1)
    .attr('stroke', '#e0e4e8')
    .attr('stroke-width', 1)
    .filter((_, i) => i === 0)
    .remove()
  svg.selectAll('.domain').attr('stroke', '#53687e').attr('stroke-width', 0)

  // Bars
  const uncertaintyParent = svg
    .selectAll('uncertaintybar')
    .data(data.filter((item) => item.uncertainty?.is_uncertain))
  uncertaintyParent
    .join('rect')
    .attr('x', 0.5)
    .attr('y', (d) => yAxisScale(d.label) ?? 0)
    .attr('height', yAxisScale.bandwidth())
    .attr('width', width)
    .attr('fill', '#d9d9d9')
    .attr('rx', yAxisScale.bandwidth() / 8)
    .attr('ry', yAxisScale.bandwidth() / 8)

  svg
    .selectAll('mybar')
    .data(data)
    .join('rect')
    .attr('x', 0.5)
    .attr('y', (d) => yAxisScale(d.label) ?? 0)
    .attr('height', yAxisScale.bandwidth())
    .attr('width', (d) => xAxisScale(d.value))
    .attr('fill', (d) => d.color)
    .attr('rx', yAxisScale.bandwidth() / 8)
    .attr('ry', yAxisScale.bandwidth() / 8)

  svg
    .selectAll('mybar')
    .data(data.filter((item) => hasErrorBar(item)))
    .enter()
    .append('path')
    .attr('d', (d) => drawErrorBar(d3.path(), d).toString())
    .attr('stroke', '#000')
    .attr('stroke-width', '2')

  // value label
  svg
    .selectAll('mybar')
    .data(data.filter((item) => !hasErrorBar(item)))
    .enter()
    .append('text')
    .attr('font-size', '8pt')
    .attr('fill', '#0b2948')
    .attr('font-weight', 'bold')
    .text((d) =>
      d.value == 0 && d.uncertainty?.is_uncertain
        ? 'Unknown'
        : xCalculator.roundValueForDisplay(d.value),
    )
    .attr('x', (d) => (d.uncertainty?.is_uncertain ? width + 16 : xAxisScale(d.value) + 8))
    .attr('y', (d) => (yAxisScale(d.label) ?? 0) + yAxisScale.bandwidth() / 2)
    .attr('dy', '0.35em')
    .attr('text-anchor', 'start')

  // Add X axis title:
  if (xTitle)
    svg
      .append('text')
      .attr('fill', '#8392a1')
      .attr('text-anchor', 'middle')
      .attr('x', width / 2)
      .attr('y', height + margin.top + 30)
      .text(xTitle)

  // Y axis title:
  if (yTitle)
    svg
      .append('text')
      .attr('fill', '#8392a1')
      .attr('text-anchor', 'middle')
      .attr('transform', 'rotate(-90)')
      .attr('y', -margin.left + 20)
      .attr('x', -height / 2)
      .text(yTitle)

  svg
    .selectAll('hitbox')
    .data(data)
    .join('rect')
    .attr('x', 0)
    .attr('y', (d) => (yAxisScale(d.label) ?? 0) - yAxisScale.bandwidth() * 0.125)
    .attr('width', width)
    .attr('height', yAxisScale.bandwidth() * 1.25)
    .attr('fill', 'transparent')
    .on('mouseover', (_e, data) => {
      if (updateTooltip)
        updateTooltip({ show: true, data, uncertaintyText: data.uncertainty?.tooltip })
    })
    .on('mouseout', (_e) => {
      if (updateTooltip) updateTooltip({ show: false })
    })
  uncertaintyParent
    .join('g')
    .attr('class', 'icon-group')
    .html(iconHTML)
    .attr('transform', (d) => {
      const x = width - 12
      const y = (yAxisScale(d.label) ?? 0) + yAxisScale.bandwidth() / 2 - 12
      return `translate(${x},${y})`
    })
    .on('mouseover', (_e, data) => {
      if (updateTooltip)
        updateTooltip({ show: true, data, uncertaintyText: data.uncertainty?.tooltip })
    })
    .on('mouseout', (_e) => {
      if (updateTooltip) updateTooltip({ show: false })
    })

  function hasErrorBar(item: ChartDataItem): boolean {
    // Has error bars if min and max are set
    // and if min and max are not equal to the value
    const hasMax = item.max !== undefined
    const hasMin = item.min !== undefined

    return hasMax && hasMin && !(item.min == item.value && item.max == item.value)
  }

  function drawErrorBar(path: d3.Path, d: ChartDataItem): d3.Path {
    const y = (yAxisScale(d.label) ?? 0) + yAxisScale.bandwidth() / 2
    const height = yAxisScale.bandwidth() / 3
    const min = d.min ?? d.value // Will never be d.value, it's here to stop whinging about d.min possibly being undefined
    const max = d.max ?? d.value

    const minX = xAxisScale(min)
    const maxX = xAxisScale(max)
    path.moveTo(minX, y - height / 2)
    path.lineTo(minX, y + height / 2)

    path.moveTo(minX, y)
    path.lineTo(maxX, y)

    path.moveTo(maxX, y - height / 2)
    path.lineTo(maxX, y + height / 2)

    return path
  }

  return { yAxisScale, xAxisScale }
}

interface BarchartProps {
  data: ChartDataItem[]
  xTitle?: string
  yTitle?: string
  tooltipFormatter?: ({ value, label, color }: ChartDataItem) => string
}

export const Barchart = ({ data, xTitle, yTitle, tooltipFormatter }: BarchartProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState<number | null>(null)
  const [showTooltip, setShowTooltip] = useState(false)
  const [tooltipItems, setTooltipItems] = useState<{ text: string; color: string }[]>([])

  const updateTooltip = useCallback(
    ({
      show,
      data,
      uncertaintyText,
    }: {
      show: boolean
      data?: ChartDataItem
      uncertaintyText?: string
    }) => {
      setShowTooltip(show)
      const newColor = data?.color ?? tooltipItems[0]?.color ?? ''
      const items = []
      if (show) {
        if (data?.value && data.value > 0) {
          if (tooltipFormatter) {
            items.push({ text: tooltipFormatter(data), color: newColor })
          } else {
            data.value = round(data.value)
            items.push({ text: `${data.label}: ${data.value}`, color: newColor })
          }
        }
        if (uncertaintyText) items.push({ text: uncertaintyText, color: '#d9d9d9' })
        const haveItemsChanged =
          items.length !== tooltipItems.length ||
          items.some((item, index) => item.text !== tooltipItems[index]?.text)
        if (haveItemsChanged) setTooltipItems(items)
      }
    },
    [tooltipFormatter, tooltipItems],
  )

  useEffect(() => {
    if (!ref.current) return
    const resizeObserver = new ResizeObserver((event) => {
      setWidth(event[0].contentBoxSize[0].inlineSize)
    })

    resizeObserver.observe(ref.current)
  }, [])

  useEffect(() => {
    if (!ref.current || width == null) return
    MakeBarChartSvg(ref.current, data, xTitle, yTitle, width, updateTooltip)
  }, [data, xTitle, yTitle, width, updateTooltip])

  return (
    <ChartTooltip show={showTooltip} items={tooltipItems}>
      <Box sx={{ width: '100%', overflow: 'hidden', position: 'relative' }}>
        <div ref={ref}></div>
      </Box>
    </ChartTooltip>
  )
}
